import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  alpha,
  TextField
} from '@mui/material';
import { Laptop, X, ChevronDown, ChevronUp, Check, AlertCircle, FileText, Link, StickyNote } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import axios from 'axios';

// Node component for displaying pages and their test cases
const PageNode = ({ page, isSelected, onSelect, selectedTestCases }) => {
  const [expanded, setExpanded] = useState(false);
  const hasTestCases = page.test_cases && page.test_cases.length > 0;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleSelect = (e) => {
    e.stopPropagation();
    onSelect(page);
  };

  const handleTestCaseSelect = (e, testCase) => {
    e.stopPropagation();
    onSelect(page, testCase);
  };

  // Check if all test cases of this page are selected
  const allTestCasesSelected = hasTestCases && 
    page.test_cases.every(testCase => selectedTestCases.has(testCase.case_id));
  
  // Check if some test cases are selected
  const someTestCasesSelected = hasTestCases && 
    page.test_cases.some(testCase => selectedTestCases.has(testCase.case_id));

  return (
    <Box>
      <Box
        onClick={hasTestCases ? handleToggle : handleSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          cursor: 'pointer',
          backgroundColor: isSelected || allTestCasesSelected ? alpha('#FFFFFF', 0.1) : 
                         someTestCasesSelected ? alpha('#FFFFFF', 0.05) : 'transparent',
          borderRadius: 1,
          transition: 'all 0.2s ease',
          border: (isSelected || allTestCasesSelected) ? `1px solid ${alpha('#FFFFFF', 0.2)}` : 
                 someTestCasesSelected ? `1px solid ${alpha('#FFFFFF', 0.1)}` : '1px solid transparent',
          '&:hover': {
            backgroundColor: (isSelected || allTestCasesSelected) ? alpha('#FFFFFF', 0.12) : alpha('#FFFFFF', 0.05)
          }
        }}
      >
        <Box 
          component="span" 
          onClick={handleSelect}
          sx={{
            width: 16,
            height: 16,
            border: '1px solid',
            borderColor: (isSelected || allTestCasesSelected) ? 'primary.main' : 
                        someTestCasesSelected ? alpha('#FFFFFF', 0.5) : 'text.secondary',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
            backgroundColor: (isSelected || allTestCasesSelected) ? '#FFFFFF' : 'transparent',
            '&:hover': {
              borderColor: 'primary.main',
            }
          }}
        >
          {(isSelected || allTestCasesSelected) && <Check size={12} color="#121212" />}
          {someTestCasesSelected && !allTestCasesSelected && (
            <Box sx={{ width: 8, height: 8, bgcolor: alpha('#FFFFFF', 0.8), borderRadius: '1px' }} />
          )}
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            flex: 1,
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: hasTestCases ? 500 : 300
          }}
        >
          {page.page.name || 'Unnamed Page'}
        </Typography>

        {hasTestCases && (
          <>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#666',
                fontSize: '0.75rem',
                ml: 1,
                mr: 2
              }}
            >
              {page.test_cases.length} Test Case{page.test_cases.length !== 1 ? 's' : ''}
            </Typography>
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{ 
                color: '#666',
                p: 0.25,
                '&:hover': {
                  backgroundColor: alpha('#FFFFFF', 0.1)
                }
              }}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </IconButton>
          </>
        )}
      </Box>

      {/* Test Cases Section */}
      {expanded && hasTestCases && (
        <Box sx={{ pl: 4, mt: 0.5 }}>
          {page.test_cases.map((testCase) => (
            <Box
              key={testCase.case_id}
              onClick={(e) => handleTestCaseSelect(e, testCase)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                cursor: 'pointer',
                backgroundColor: selectedTestCases.has(testCase.case_id) ? alpha('#FFFFFF', 0.1) : 'transparent',
                borderRadius: 1,
                transition: 'all 0.2s ease',
                border: selectedTestCases.has(testCase.case_id) ? `1px solid ${alpha('#FFFFFF', 0.2)}` : '1px solid transparent',
                '&:hover': {
                  backgroundColor: selectedTestCases.has(testCase.case_id) ? alpha('#FFFFFF', 0.12) : alpha('#FFFFFF', 0.05)
                },
                mb: 0.5
              }}
            >
              <Box 
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTestCaseSelect(e, testCase);
                }}
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid',
                  borderColor: selectedTestCases.has(testCase.case_id) ? 'primary.main' : 'text.secondary',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  backgroundColor: selectedTestCases.has(testCase.case_id) ? '#FFFFFF' : 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                {selectedTestCases.has(testCase.case_id) && <Check size={12} color="#121212" />}
              </Box>
              
              <FileText size={14} style={{ color: '#666', marginRight: 8 }} />
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 300
                }}
              >
                {testCase.test_guide || 'Test Case'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

function AddTestGroupModal({ open, onClose }) {
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [selectedTestCases, setSelectedTestCases] = useState(new Set());
  const [loadingPages, setLoadingPages] = useState(false);
  const [pagesError, setPagesError] = useState(null);
  
  // New state for App URL and Notes
  const [appUrl, setAppUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchStudios();
    } else {
      // Reset states when modal closes
      setSelectedPages(new Set());
      setSelectedTestCases(new Set());
      setPagesError(null);
    }
  }, [open]);

  useEffect(() => {
    if (selectedStudio) {
      fetchPages();
    }
  }, [selectedStudio]);

  const fetchStudios = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/studio/seeds`, { headers });
      
      if (response.data) {
        const readyStudios = response.data.filter(studio => studio.status === "READY");
        setStudios(readyStudios);
        if (readyStudios.length > 0) {
          setSelectedStudio(readyStudios[0].id);
        }
      }
    } catch (err) {
      console.error("Error fetching studios:", err);
      setError(err.message || "Failed to fetch studios");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPages = async () => {
    setLoadingPages(true);
    setPagesError(null);
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/studios/${selectedStudio}/pages_with_test_cases`, { headers });
      
      if (response.data?.pages_with_test_cases) {
        setPages(response.data.pages_with_test_cases);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
      setPagesError(err.message || "Failed to fetch pages");
    } finally {
      setLoadingPages(false);
    }
  };

  const handleStudioChange = (event) => {
    setSelectedStudio(event.target.value);
    setSelectedPages(new Set());
    setSelectedTestCases(new Set());
  };

  const handleSelect = (page, testCase = null) => {
    if (testCase) {
      // Handle test case selection
      setSelectedTestCases(prev => {
        const newSet = new Set(prev);
        if (newSet.has(testCase.case_id)) {
          newSet.delete(testCase.case_id);
        } else {
          newSet.add(testCase.case_id);
        }
        return newSet;
      });
    } else {
      // Handle page selection
      setSelectedPages(prev => {
        const newSet = new Set(prev);
        if (newSet.has(page.page.page_id)) {
          newSet.delete(page.page.page_id);
          // Also deselect all test cases for this page
          if (page.test_cases) {
            setSelectedTestCases(prev => {
              const newTestCases = new Set(prev);
              page.test_cases.forEach(testCase => {
                newTestCases.delete(testCase.case_id);
              });
              return newTestCases;
            });
          }
        } else {
          newSet.add(page.page.page_id);
          // Also select all test cases for this page
          if (page.test_cases) {
            setSelectedTestCases(prev => {
              const newTestCases = new Set(prev);
              page.test_cases.forEach(testCase => {
                newTestCases.add(testCase.case_id);
              });
              return newTestCases;
            });
          }
        }
        return newSet;
      });
    }
  };

  const handleCreate = async () => {
    setIsProcessing(true);
    setCreateError(null);
    
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        studio_id: selectedStudio,
        test_cases: Array.from(selectedTestCases),
        app_url: appUrl.trim(),
        notes: notes.trim()
      };

      const response = await axios.post(
        `${API_URL}/test_e2e`,
        payload,
        { headers }
      );

      if (response.status === 200) {
        // Pass the newly created group ID to the parent component
        const newGroupId = response.data?.group_id;
        onClose(newGroupId);
      } else {
        throw new Error(response.data?.error || 'Failed to create test group');
      }
    } catch (error) {
      console.error('Error creating test group:', error);
      setCreateError(error.message || 'Failed to create test group');
    } finally {
      setIsProcessing(false);
    }
  };

  const getSelectedCount = () => {
    return selectedTestCases.size;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <Box
        sx={{
          backgroundColor: '#121212',
          border: '1px solid #333',
          borderRadius: 2,
          p: 4,
          maxWidth: '600px',
          width: '90%',
          position: 'relative',
          outline: 'none',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#666',
          }}
        >
          <X size={20} />
        </IconButton>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Laptop size={24} style={{ color: '#666' }} />
          <Typography
            variant="h6"
            component="h2"
            sx={{
              color: '#fff',
              fontWeight: 600,
            }}
          >
            Create New Test Group
          </Typography>
        </Box>

        <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            </Box>
          ) : error ? (
            <Typography sx={{ color: 'error.main', textAlign: 'center', py: 2 }}>
              {error}
            </Typography>
          ) : (
            <Select
              value={selectedStudio}
              onChange={handleStudioChange}
              sx={{
                backgroundColor: '#121212',
                border: '1px solid #333',
                borderRadius: 1,
                color: '#fff',
                '& .MuiSelect-icon': {
                  color: '#666',
                },
                '&:hover': {
                  borderColor: '#666',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              {studios.map((studio) => (
                <MenuItem 
                  key={studio.id} 
                  value={studio.id}
                  sx={{
                    backgroundColor: '#121212',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#252525',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#252525',
                    },
                  }}
                >
                  {studio.display_name}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        {selectedStudio && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#888',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                Select pages and test cases to include in the test group
              </Typography>

              <Box sx={{ 
                bgcolor: alpha('#FFFFFF', 0.01),
                borderRadius: 1,
                border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {loadingPages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  </Box>
                ) : pagesError ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ color: 'error.main' }}>
                      {pagesError}
                    </Typography>
                  </Box>
                ) : pages.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ color: '#666' }}>
                      No pages found with test cases
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1 }}>
                    {pages.map((page) => (
                      <PageNode
                        key={page.page.page_id}
                        page={page}
                        isSelected={selectedPages.has(page.page.page_id)}
                        selectedTestCases={selectedTestCases}
                        onSelect={handleSelect}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Link size={16} style={{ color: '#666' }} />
                <Typography variant="body2" sx={{ color: '#888' }}>
                  App URL
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder="Enter the application URL"
                value={appUrl}
                onChange={(e) => setAppUrl(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: 1,
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#666',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StickyNote size={16} style={{ color: '#666' }} />
                <Typography variant="body2" sx={{ color: '#888' }}>
                  Notes
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add any login credentials or important information needed for testing"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: 1,
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#666',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                }}
              />
            </Box>

            {createError && (
              <Box sx={{ 
                mb: 3,
                p: 2,
                bgcolor: alpha('#FF4D4F', 0.1),
                borderRadius: 1,
                border: `1px solid ${alpha('#FF4D4F', 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <AlertCircle size={16} color="#FF4D4F" />
                <Typography variant="body2" sx={{ color: '#FF4D4F' }}>
                  {createError}
                </Typography>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleCreate}
              disabled={isProcessing || getSelectedCount() === 0 || !appUrl.trim()}
              sx={{
                backgroundColor: '#121212',
                border: '1px solid #333',
                borderRadius: 1,
                color: '#fff',
                py: 1.5,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#252525',
                  borderColor: '#666',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#121212',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                `Create Test Group (${getSelectedCount()} test cases)`
              )}
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default AddTestGroupModal; 